// src/padplanner/MapViewToggle.jsx
//
// Imagery | Basemap | Paper (mapView.js), top right on the map, above the
// basemap picker it governs; in Paper, a Dark | Light switch (paperTheme.js)
// and an "Outlines" switch under it. And
// Paper's legend, bottom right over the scale bar: the grid's spacings at
// this zoom (paperGrid.js legendText) and an arrow at grid north.
import { MAP_VIEW_MODES, MAP_VIEW_LABELS } from "./mapView";
import { PAPER_TONES, PAPER_TONE_LABELS } from "./paperTheme";
import { legendText } from "./paperGrid";

/**
 * align: {checked, onChange} offers "Align grid to pad" in Paper, for a
 * read-only page (the editor's is in its Snap menu, where it snaps too).
 */
export default function MapViewToggle({ view, align = null }) {
  return (
    <div className="flex flex-col items-end gap-1" data-testid="pp-view-toggle">
      <div className="flex overflow-hidden rounded-md border border-bg-border bg-bg/90 shadow" role="group" aria-label="Map view">
        {MAP_VIEW_MODES.map((m) => (
          <button key={m} type="button" aria-pressed={view.mode === m} data-testid="pp-view-mode" data-mode={m}
            onClick={() => view.setMode(m)}
            className={`px-2.5 py-1 text-[12px] ${view.mode === m ? "bg-accent-action/20 text-accent-action" : "text-fg-secondary hover:text-fg-primary"}`}>
            {MAP_VIEW_LABELS[m]}
          </button>
        ))}
      </div>
      {view.mode === "paper" && (
        <div className="flex overflow-hidden rounded-md border border-bg-border bg-bg/90 shadow" role="group" aria-label="Paper" data-testid="pp-paper-tone">
          {PAPER_TONES.map((t) => (
            <button key={t} type="button" aria-pressed={view.tone === t} data-testid="pp-paper-tone-option" data-tone={t}
              onClick={() => view.setTone(t)}
              className={`px-2 py-0.5 text-[12px] ${view.tone === t ? "bg-accent-action/20 text-accent-action" : "text-fg-secondary hover:text-fg-primary"}`}>
              {PAPER_TONE_LABELS[t]}
            </button>
          ))}
        </div>
      )}
      {view.mode === "paper" && (
        <label className="flex items-center gap-1.5 rounded bg-bg/90 px-2 py-0.5 text-[12px] text-fg-secondary">
          <input type="checkbox" checked={view.outlines} onChange={(e) => view.setOutlines(e.target.checked)} data-testid="pp-paper-outlines" />
          Outlines
        </label>
      )}
      {view.mode === "paper" && align && (
        <label className="flex items-center gap-1.5 rounded bg-bg/90 px-2 py-0.5 text-[12px] text-fg-secondary" title={align.title ?? undefined}>
          <input type="checkbox" checked={align.checked} disabled={align.disabled} onChange={(e) => align.onChange(e.target.checked)} data-testid="pp-paper-align" />
          Align grid to pad
        </label>
      )}
    </div>
  );
}

/** tier: paperGrid.js gridTier()'s, or null; northDeg: the grid's up from true north, clockwise; axisDeg: the pad axis, when aligned. */
export function PaperLegend({ tier, northDeg, axisDeg = null }) {
  const text = legendText(tier, axisDeg);
  if (!text) return null;
  return (
    <p className="flex items-center gap-1.5 rounded bg-bg/85 px-2 py-0.5 text-[11px] text-fg-secondary" data-testid="pp-paper-legend">
      <span aria-hidden="true" className="inline-block" style={{ transform: `rotate(${northDeg ?? 0}deg)` }} data-testid="pp-grid-north">↑</span>
      <span>{text}</span>
    </p>
  );
}
